import { zarazService } from "@/services";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useSelector } from 'react-redux';
import CustomModal from '../../../components/tailwind/CustomModal';

type CustomShopModalForwardedRefs = {
  openModal: () => void;
  closeModal: () => void;
};

type CustomShopModalProps = {
  onAccept: () => void;
  onCancel: () => void;
  quantity: number;
};

const CustomShopModal: React.ForwardRefRenderFunction<CustomShopModalForwardedRefs, CustomShopModalProps> = (
  { onAccept, onCancel, quantity },
  forwardedRef
) => {
  const customModalRef = useRef(null);
  const { details } = useSelector((state: any) => state.pdpItem);

  useImperativeHandle(
    forwardedRef,
    () => ({
      openModal: customModalRef.current.openModal,
      closeModal: onDismissClick,
    }),
    [customModalRef]
  );

  const trackPopupInteraction= (eventName: string) => {
    zarazService.track(eventName, {
      clientUri: window.location.pathname + window.location.search,
      itemId: details?.itemId,
    });
  }

  useEffect(() => {
    trackPopupInteraction('myrtic_customshop_popup_display');
  }, []);

  const onAcceptClick = () => {
    customModalRef.current.closeModal();
    onAccept();
    trackPopupInteraction('myrtic_customshop_popup_click_getquote');
  };

  const onRejectionClick = () => {
    customModalRef.current.closeModal();
    onCancel();
    trackPopupInteraction('myrtic_customshop_popup_click_nothanks');
  };

  const onDismissClick = () => {
    trackPopupInteraction('myrtic_customshop_popup_close');
    onCancel();
  };

  return (
    <CustomModal
      ref={customModalRef}
      titleClassName='tw-py-4'
      dialogClassName='tw-mx-3 lg:tw-max-w-lg'
      removeBodyPadding
      onDismissed={onDismissClick}
      id='CustomShopModal'
      title={
        <span className='tw-flex tw-items-center tw-space-x-2'>
          <img
            src='/assets/images/rtic/rtic-custom-colored.svg'
            alt='MyRTIC'
            className='tw-block tw-h-[9px] tw-flex-none'
          />
          <span className='tw-text-[13px] tw-font-bold tw-leading-none'>RTIC Custom Shop</span>
        </span>
      }
      size='lg'>
      <div className='tw-px-6 tw-pb-4 tw-pt-6'>
        <div className='tw-flex'>
          <div>
            <img
              src={details.webImages.default.thumb.url}
              alt={details.displayName}
              className='tw-w-[75px]'
            />
          </div>
          <div className='tw-pl-2.5 tw-pt-1'>
            <div className='tw-text-[13px] tw-font-bold'>{details.displayName}</div>
            <div className='tw-text-[13px] tw-font-semibold'>QTY: {quantity}</div>
          </div>
        </div>
        <hr className='tw-mb-5 tw-mt-2.5' />
        <p className='tw-text-center tw-text-xs'>
          Looks like you&apos;re buying a lot! Check out our Custom Shop for bulk pricing, design assistance,
          promotional material stuffing, and drop shipping options.
        </p>
        <div className='mt-4 text-center'>
          <button onClick={onAcceptClick} className='twc-btn twc-btn--primary tw-block tw-w-full'>
            Get a quote
          </button>
          <button
            onClick={onRejectionClick}
            className='tw-mt-1 tw-px-6 tw-py-3 tw-text-[13px] tw-font-bold tw-uppercase tw-text-[#303030] tw-transition hover:tw-opacity-80'>
            No Thanks
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

const ForwardedCustomShopModal = forwardRef(CustomShopModal);
export type CustomShopModalRef = React.ElementRef<typeof ForwardedCustomShopModal>;
export default ForwardedCustomShopModal;
